import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Calendar, 
  Users, 
  Settings, 
  Clock, 
  FileText,
  Stethoscope,
  LogOut,
  MapPin,
  Zap,
  Bot,
  Bell,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  CheckCircle,
  MessageSquare,
  User,
  RefreshCw,
  Building2,
  Sun,
  Moon
} from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../supabaseClient';
import './Sidebar.css';

const WhatsAppIcon = ({ size = 20, className = "" }) => (
  <svg 
    viewBox="0 0 24 24" 
    width={size} 
    height={size} 
    fill="currentColor" 
    className={className}
    style={{ display: 'inline-block', verticalAlign: 'middle' }}
  >
    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.514 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.73-1.455L0 24zm6.59-4.846c1.6.95 3.473 1.452 5.38 1.453 5.485 0 9.948-4.469 9.952-9.953.002-2.657-1.02-5.156-2.88-7.019-1.86-1.862-4.361-2.885-7.025-2.887-5.49 0-9.96 4.47-9.964 9.956-.001 1.933.504 3.82 1.464 5.432l-.962 3.51 3.593-.943zm11.418-6.126c-.3-.15-1.77-.875-2.04-.973-.27-.1-.47-.15-.67.15-.2.3-.77.973-.94 1.172-.17.2-.34.225-.64.075-.3-.15-1.265-.465-2.41-1.485-.89-.795-1.49-1.78-1.66-2.08-.17-.3-.02-.465.13-.615.135-.135.3-.35.45-.525.15-.175.2-.3.3-.5.1-.2.05-.375-.025-.525-.075-.15-.67-1.62-.92-2.2-.24-.585-.48-.5-.67-.51-.17-.01-.37-.01-.57-.01-.2 0-.52.075-.79.375-.27.3-1.03 1.01-1.03 2.46 0 1.45 1.05 2.85 1.2 3.05.15.2 2.07 3.16 5.015 4.43.7.3 1.245.48 1.67.615.71.22 1.35.19 1.86.115.57-.085 1.77-.725 2.02-1.39.25-.665.25-1.235.175-1.39-.075-.15-.27-.25-.57-.4z"/>
  </svg>
);

const Sidebar = ({ isCollapsed = false, onToggle }) => {
  const navigate = useNavigate();
  const { notifications } = useNotifications();
  const { theme, toggleTheme } = useTheme();
  const [pendingReminders, setPendingReminders] = React.useState(0);

  React.useEffect(() => {
    const fetchPendingCount = async () => {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toISOString().split('T')[0];

      try {
        const { count, error } = await supabase
          .from('appointments')
          .select('*', { count: 'exact', head: true })
          .eq('appointment_date', tomorrowStr)
          .eq('verification_status', 'not_verified');
        
        if (!error && count !== null) {
          setPendingReminders(count);
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchPendingCount();
    const interval = setInterval(fetchPendingCount, 60000);
    return () => clearInterval(interval);
  }, []);

  const activeStaff = JSON.parse(localStorage.getItem('gdt_active_staff') || '{}');
  const activeUser = JSON.parse(localStorage.getItem('gdt_active_user') || '{}');
  
  const isUserRoleUser = activeUser?.role === 'user';
  const isUserRoleAdmin = activeUser?.role === 'admin' || activeUser?.role === 'administrador' || activeUser?.role === 'secretaria' || activeUser?.role === 'webmaster' || activeUser?.role === 'superadmin';
  
  const isAdmin = isUserRoleUser 
    ? false 
    : (isUserRoleAdmin || activeStaff?.type === 'Administrador' || activeStaff?.type === 'Secretaria/o' || activeStaff?.type === 'Webmaster');

  const initials = activeUser?.username 
    ? activeUser.username.substring(0, 2).toUpperCase() 
    : 'AD';
    
  const displayName = activeUser?.username 
    ? activeUser.username.charAt(0).toUpperCase() + activeUser.username.slice(1)
    : 'Administrador';

  const displayRole = activeUser?.username
    ? (activeUser.role === 'webmaster' ? `${activeUser.username} (Webmaster)` : isUserRoleUser ? `${activeUser.username} (Usuario)` : `${activeUser.username} (Admin)`)
    : (activeStaff?.full_name || 'Clínica Médica GDT');

  const handleLogout = () => {
    localStorage.removeItem('gdt_active_staff');
    localStorage.removeItem('gdt_active_user');
    navigate('/login/staff');
  };

  const confirmedNotifs = notifications.filter(n => !n.read && n.title && n.title.toLowerCase().includes('confirmado')).length;
  const agendaNotifs = notifications.filter(n => !n.read && n.title && (n.title.toLowerCase().includes('nuevo turno') || n.title.toLowerCase().includes('cancelado') || n.title.toLowerCase().includes('agendado'))).length;

  return (
    <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        <div className="logo-container">
          <div className="logo-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <div className="logo">
              <div className="logo-icon"><Zap size={20} fill="var(--primary-cyan)" /></div>
              {!isCollapsed && <span>GDT</span>}
            </div>
            {onToggle && (
              <button 
                type="button" 
                className="sidebar-toggle-btn" 
                onClick={onToggle}
                title={isCollapsed ? "Desplegar menú lateral" : "Ocultar menú lateral"}
              >
                {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
              </button>
            )}
          </div>
          {!isCollapsed && (
            <div className="professional-indicator" onClick={() => navigate('/login/staff')} style={{ cursor: 'pointer' }}>
              <span>{activeStaff?.full_name?.toUpperCase() || 'ELEGIR STAFF'}</span>
              <ChevronDown size={14} className="chevron-small" />
            </div>
          )}
        </div>
      </div>
      
      <div className="sidebar-scrollable">
        <nav className="sidebar-nav">
          <NavLink to="/dashboard" title="Dashboard" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
            <LayoutDashboard size={20} /> <span className="nav-label">Dashboard</span>
          </NavLink>
          
          <NavLink to="/calendar" title="Agendas de Turnos" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
            <Calendar size={20} /> <span className="nav-label">Agendas de Turnos</span>
            {agendaNotifs > 0 && <span className="sidebar-badge">{agendaNotifs}</span>}
          </NavLink>
          
          <NavLink to="/confirmed" title="Turnos Confirmados" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
            <CheckCircle size={20} /> <span className="nav-label">Turnos Confirmados</span>
            {confirmedNotifs > 0 && <span className="sidebar-badge">{confirmedNotifs}</span>}
          </NavLink>
          
          <NavLink to="/waiting-room" title="Sala de Espera Virtual" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
            <Clock size={20} /> <span className="nav-label">Sala de Espera Virtual</span>
          </NavLink>
          
          <NavLink to="/patients" title="Pacientes" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
            <Users size={20} /> <span className="nav-label">Pacientes</span>
          </NavLink>

          {isAdmin && (
            <>
              <NavLink to="/accounting" title="Contabilidad" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <DollarSign size={20} /> <span className="nav-label">Contabilidad</span>
              </NavLink>

              <NavLink to="/companies" title="Empresas Registradas" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <Building2 size={20} /> <span className="nav-label">Empresas Registradas</span>
              </NavLink>

              <NavLink to="/staff" title="Staff del Consultorio" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <User size={20} /> <span className="nav-label">Staff del Consultorio</span>
              </NavLink>

              <NavLink to="/reports" title="Listados" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <FileText size={20} /> <span className="nav-label">Listados</span>
              </NavLink>
            </>
          )}

          <NavLink to="/whatsapp-manual" title="Recordatorios WhatsApp" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
            <WhatsAppIcon size={20} className="nav-icon" /> <span className="nav-label">Record. x WhatsApp manuales</span>
          </NavLink>

          <NavLink to="/reminders" title="Recordatorios de Turnos" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
            <Bell size={20} /> <span className="nav-label">Recordatorios de Turnos</span>
            {pendingReminders > 0 && <span className="sidebar-badge">{pendingReminders}</span>}
          </NavLink>

          <div className="nav-divider"></div>

          <NavLink to="/settings" state={{ tab: 'ai' }} title="NEON AI Copilot" className={({ isActive }) => `nav-item ai-nav-item ${isActive ? 'active' : ''}`}>
            <Bot size={20} className="ai-icon-glow" /> <span className="nav-label">NEON AI Copilot</span>
          </NavLink>

          {isAdmin && (
            <NavLink to="/settings" title="Configuración" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
              <Settings size={20} /> <span className="nav-label">Configuración</span>
            </NavLink>
          )}
        </nav>
      </div>

      <div className="sidebar-footer">
        <div className="user-profile" title={`${displayName} - ${displayRole}`}>
          <div className="avatar">{initials}</div>
          {!isCollapsed && (
            <div className="user-info">
              <p className="user-name">{displayName}</p>
              <p className="user-role" title={displayRole}>{displayRole}</p>
            </div>
          )}
        </div>
        <div 
          className="nav-item theme-toggle-btn" 
          title={theme === 'dark' ? 'CAMBIAR A MODO CLARO' : 'CAMBIAR A MODO OSCURO'} 
          onClick={toggleTheme}
          style={{ cursor: 'pointer', margin: '4px 0' }}
        >
          {theme === 'dark' ? <Sun size={18} style={{ color: '#ffca28' }} /> : <Moon size={18} style={{ color: '#00838f' }} />}
          <span className="nav-label">{theme === 'dark' ? 'MODO CLARO ☀️' : 'MODO OSCURO 🌙'}</span>
        </div>
        <div className="nav-item logout" title="SALIR" onClick={handleLogout}>
          <LogOut size={18} />
          <span className="nav-label">SALIR</span>
        </div>
        <div className="nav-item force-update-btn" title="FORZAR ACTUALIZACIÓN" style={{ marginTop: '5px', fontSize: '12px' }} onClick={() => {
          if (window.caches) {
            window.caches.keys().then((names) => {
              for (let name of names) window.caches.delete(name);
            });
          }
          window.location.href = window.location.pathname + '?v=' + new Date().getTime() + window.location.hash;
        }}>
          <RefreshCw size={14} />
          <span className="nav-label">FORZAR ACTUALIZACIÓN</span>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
